/**
 * Synthetic anomalies across the three Meridian sites.
 *
 * Units are authored per site and never converted. Note ALR-4471 (Findlay) and
 * ALR-4468 (Weißenthurm) — the same bearing-wear signature on the same class of
 * machine, one expressed natively in in/s against a 0.49 in/s threshold, the
 * other in mm/s against 12.5 mm/s. Those two numbers describe the same physical
 * limit. Displaying either one in the other's unit without its canonical value
 * alongside would be a safety defect, so the fixture keeps them separate and
 * the UI never converts.
 */

export const SEVERITIES = ['critical', 'high', 'medium', 'low']
export const STATUSES = ['open', 'ack', 'escalated', 'resolved']

/** Shape glyphs — the redundant encoding that survives colour-blind mode. */
export const SEVERITY_GLYPH = { critical: '◆', high: '▲', medium: '●', low: '■', ok: '✓' }

export const ALERTS = [
  {
    id: 'ALR-4471',
    severity: 'critical',
    status: 'open',
    assigneeId: null,
    siteId: 'site_findlay',
    lineId: 'line_fdy_1',
    lineName: 'Line 1',
    assetId: 'FDY-BM-03',
    assetName: 'Bodymaker 3',
    component: 'Discharge bearing',
    sensor: 'Vibration RMS',
    sensorTag: 'ns=2;s=PLC1.FDY.BM03.VibX',
    anomalyType: 'sustainedExceedance',
    reading: { value: 0.6, unit: 'in/s' },
    threshold: { value: 0.49, unit: 'in/s' },
    baseline: { value: 0.11, unit: 'in/s' },
    detectedMinutesAgo: 34,
    etaHours: 4.5,
    fusionScore: 94,
    detectors: [
      { name: 'deltaZ', score: 11.12, unit: 'σ' },
      { name: 'cusum', score: 2.31, unit: null },
      { name: 'pageHinkley', score: 8.74, unit: null },
      { name: 'isolationForest', score: 0.91, unit: null },
    ],
    qualified: true,
    rule: 'rule2CascadeSignature',
    cascadeSensors: 4,
    slopePerHour: { value: 0.013, unit: 'in/s' },
    correlation: 0.87,
    downtimeRiskHours: 6.5,
    hasAnalysis: true,
  },
  {
    id: 'ALR-4470',
    severity: 'critical',
    status: 'open',
    assigneeId: 'usr_rel_1',
    siteId: 'site_findlay',
    lineId: 'line_fdy_util',
    lineName: 'Compressed Air',
    assetId: 'FDY-CMP-12',
    assetName: 'Compressor C-12',
    component: 'Coolant loop',
    sensor: 'Coolant Flow',
    sensorTag: 'ns=2;s=PLC4.FDY.CMP12.FlowA',
    anomalyType: 'frozenSignal',
    reading: { value: 0, unit: 'gal/min' },
    threshold: { value: 1.3, unit: 'gal/min' },
    baseline: { value: 4.2, unit: 'gal/min' },
    detectedMinutesAgo: 78,
    etaHours: 2.0,
    fusionScore: 88,
    detectors: [
      { name: 'deltaZ', score: 9.4, unit: 'σ' },
      { name: 'pageHinkley', score: 12.02, unit: null },
    ],
    qualified: true,
    rule: 'flatlineWithLiveNeighbours',
    cascadeSensors: 3,
    downtimeRiskHours: 8.0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4465',
    severity: 'critical',
    status: 'escalated',
    assigneeId: 'usr_mgr_1',
    siteId: 'site_findlay',
    lineId: 'line_fdy_1',
    lineName: 'Line 1',
    assetId: 'FDY-OVN-01',
    assetName: 'IBO Oven',
    component: 'Zone 3 burner',
    sensor: 'Zone 3 Temp',
    sensorTag: 'ns=2;s=PLC2.FDY.OVN01.TZ3',
    anomalyType: 'thermalDrift',
    reading: { value: 402, unit: '°F' },
    threshold: { value: 385, unit: '°F' },
    baseline: { value: 368, unit: '°F' },
    detectedMinutesAgo: 512,
    etaHours: null,
    fusionScore: 76,
    detectors: [
      { name: 'cusum', score: 3.88, unit: null },
      { name: 'deltaZ', score: 5.21, unit: 'σ' },
    ],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 1,
    downtimeRiskHours: 3.0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4468',
    severity: 'high',
    status: 'open',
    assigneeId: 'usr_rel_1',
    siteId: 'site_weissenthurm',
    lineId: 'line_wtm_1',
    lineName: 'Ends Line 1',
    assetId: 'WTM-PRS-04',
    assetName: 'Shell Press 4',
    component: 'Main drive bearing',
    sensor: 'Vibration RMS',
    sensorTag: 'ns=2;s=PLC1.WTM.PRS04.VibY',
    anomalyType: 'bearingWear',
    reading: { value: 14.8, unit: 'mm/s' },
    threshold: { value: 12.5, unit: 'mm/s' },
    baseline: { value: 3.2, unit: 'mm/s' },
    detectedMinutesAgo: 196,
    etaHours: 19.0,
    fusionScore: 81,
    detectors: [
      { name: 'deltaZ', score: 6.02, unit: 'σ' },
      { name: 'cusum', score: 1.94, unit: null },
      { name: 'isolationForest', score: 0.77, unit: null },
    ],
    qualified: true,
    rule: 'rule2CascadeSignature',
    cascadeSensors: 3,
    downtimeRiskHours: 4.5,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4463',
    severity: 'high',
    status: 'ack',
    assigneeId: 'usr_mgr_1',
    siteId: 'site_findlay',
    lineId: 'line_fdy_1',
    lineName: 'Line 1',
    assetId: 'FDY-DEC-02',
    assetName: 'Decorator 2',
    component: 'Ink pump 2',
    sensor: 'Ink Pump Pressure',
    sensorTag: 'ns=2;s=PLC3.FDY.DEC02.PrsInk',
    anomalyType: 'pressureSurge',
    reading: { value: 81, unit: 'psi' },
    threshold: { value: 72, unit: 'psi' },
    baseline: { value: 64, unit: 'psi' },
    detectedMinutesAgo: 640,
    etaHours: null,
    fusionScore: 69,
    detectors: [{ name: 'deltaZ', score: 4.33, unit: 'σ' }],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 1,
    downtimeRiskHours: 1.5,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4460',
    severity: 'high',
    status: 'open',
    assigneeId: null,
    siteId: 'site_findlay',
    lineId: 'line_fdy_1',
    lineName: 'Line 1',
    assetId: 'FDY-WSH-01',
    assetName: 'Washer',
    component: 'Stage 4 rinse',
    sensor: 'Conductivity',
    sensorTag: 'ns=2;s=PLC2.FDY.WSH01.Cond4',
    anomalyType: 'contamination',
    reading: { value: 620, unit: 'µS/cm' },
    threshold: { value: 500, unit: 'µS/cm' },
    baseline: { value: 310, unit: 'µS/cm' },
    detectedMinutesAgo: 305,
    etaHours: null,
    fusionScore: 72,
    detectors: [
      { name: 'cusum', score: 2.66, unit: null },
      { name: 'deltaZ', score: 4.9, unit: 'σ' },
    ],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 2,
    downtimeRiskHours: 2.0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4459',
    severity: 'medium',
    status: 'open',
    assigneeId: 'usr_ops_1',
    siteId: 'site_pune',
    lineId: 'line_pnq_1',
    lineName: 'Line 1',
    assetId: 'PNQ-BM-02',
    assetName: 'Bodymaker 2',
    component: 'Coolant supply',
    sensor: 'Coolant Flow',
    sensorTag: 'ns=2;s=PLC1.PNQ.BM02.FlowIn',
    anomalyType: 'flowRestriction',
    reading: { value: 41, unit: 'L/min' },
    threshold: { value: 48, unit: 'L/min' },
    baseline: { value: 52, unit: 'L/min' },
    detectedMinutesAgo: 150,
    etaHours: 26.0,
    fusionScore: 58,
    detectors: [{ name: 'deltaZ', score: 3.41, unit: 'σ' }],
    qualified: true,
    rule: 'iowLowerBreach',
    cascadeSensors: 2,
    downtimeRiskHours: 1.0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4457',
    severity: 'medium',
    status: 'ack',
    assigneeId: 'usr_rel_1',
    siteId: 'site_weissenthurm',
    lineId: 'line_wtm_1',
    lineName: 'Ends Line 1',
    assetId: 'WTM-CVR-02',
    assetName: 'Conversion Press 2',
    component: 'Hydraulic pack',
    sensor: 'Hydraulic Temp',
    sensorTag: 'ns=2;s=PLC2.WTM.CVR02.THyd',
    anomalyType: 'thermalDrift',
    reading: { value: 71, unit: '°C' },
    threshold: { value: 65, unit: '°C' },
    baseline: { value: 58, unit: '°C' },
    detectedMinutesAgo: 430,
    etaHours: null,
    fusionScore: 54,
    detectors: [{ name: 'cusum', score: 1.72, unit: null }],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 1,
    downtimeRiskHours: 0.5,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4452',
    severity: 'medium',
    status: 'open',
    assigneeId: null,
    siteId: 'site_findlay',
    lineId: 'line_fdy_2',
    lineName: 'Line 2',
    assetId: 'FDY-PAL-02',
    assetName: 'Palletizer',
    component: 'Layer gripper',
    sensor: 'Acoustic Emission',
    sensorTag: 'ns=2;s=PLC5.FDY.PAL02.AE1',
    anomalyType: 'oscillation',
    reading: { value: 96, unit: 'dB' },
    threshold: { value: 90, unit: 'dB' },
    baseline: { value: 82, unit: 'dB' },
    detectedMinutesAgo: 900,
    etaHours: null,
    fusionScore: 47,
    detectors: [{ name: 'isolationForest', score: 0.63, unit: null }],
    qualified: true,
    rule: 'spectralOutlier',
    cascadeSensors: 1,
    downtimeRiskHours: 0.5,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4448',
    severity: 'low',
    status: 'open',
    assigneeId: 'usr_ops_1',
    siteId: 'site_pune',
    lineId: 'line_pnq_1',
    lineName: 'Line 1',
    assetId: 'PNQ-NCK-01',
    assetName: 'Necker',
    component: 'Spindle 6',
    sensor: 'Spindle Vibration',
    sensorTag: 'ns=2;s=PLC1.PNQ.NCK01.Vib6',
    anomalyType: 'oscillation',
    reading: { value: 4.1, unit: 'mm/s' },
    threshold: { value: 3.8, unit: 'mm/s' },
    baseline: { value: 2.6, unit: 'mm/s' },
    detectedMinutesAgo: 1320,
    etaHours: null,
    fusionScore: 31,
    detectors: [{ name: 'deltaZ', score: 2.18, unit: 'σ' }],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 1,
    downtimeRiskHours: 0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4444',
    severity: 'low',
    status: 'ack',
    assigneeId: 'usr_rel_1',
    siteId: 'site_findlay',
    lineId: 'line_fdy_util',
    lineName: 'Compressed Air',
    assetId: 'FDY-CHL-02',
    assetName: 'Chiller CH-02',
    component: 'Refrigerant circuit',
    sensor: 'Suction Pressure',
    sensorTag: 'ns=2;s=PLC4.FDY.CHL02.PrsSuc',
    anomalyType: 'drift',
    reading: { value: 118, unit: 'psi' },
    threshold: { value: 112, unit: 'psi' },
    baseline: { value: 104, unit: 'psi' },
    detectedMinutesAgo: 2100,
    etaHours: null,
    fusionScore: 28,
    detectors: [{ name: 'cusum', score: 1.14, unit: null }],
    qualified: true,
    rule: 'iowUpperBreach',
    cascadeSensors: 1,
    downtimeRiskHours: 0,
    hasAnalysis: false,
  },
  {
    id: 'ALR-4441',
    severity: 'high',
    status: 'resolved',
    assigneeId: 'usr_mgr_1',
    siteId: 'site_findlay',
    lineId: 'line_fdy_2',
    lineName: 'Line 2',
    assetId: 'FDY-BM-07',
    assetName: 'Bodymaker 7',
    component: 'Ram bearing',
    sensor: 'Vibration RMS',
    sensorTag: 'ns=2;s=PLC1.FDY.BM07.VibX',
    anomalyType: 'bearingWear',
    reading: { value: 0.52, unit: 'in/s' },
    threshold: { value: 0.49, unit: 'in/s' },
    baseline: { value: 0.12, unit: 'in/s' },
    detectedMinutesAgo: 4600,
    etaHours: null,
    fusionScore: 84,
    detectors: [
      { name: 'deltaZ', score: 7.11, unit: 'σ' },
      { name: 'cusum', score: 2.02, unit: null },
    ],
    qualified: true,
    rule: 'rule2CascadeSignature',
    cascadeSensors: 3,
    downtimeRiskHours: 0,
    resolution: 'plannedReplacement',
    hasAnalysis: false,
  },
]

/**
 * The flagship analysis. This is the product's whole claim in one object: which
 * detectors fired, the gate they passed, the baseline compared against, the
 * tools the agent called, and the named rule that produced the verdict.
 */
export const ANALYSIS = {
  'ALR-4471': {
    alertId: 'ALR-4471',
    authoredBy: 'ai',
    confidence: 0.94,
    rootCause: 'progressiveDischargeBearingWear',
    ruledOut: ['sensorFault', 'looseMount', 'processChange'],
    toolCalls: [
      { tool: 'querySensorHistory', args: '72h · 4 tags', ms: 412 },
      { tool: 'compareBaseline', args: 'trailing 30d, same product', ms: 118 },
      { tool: 'correlateSiblings', args: 'FDY-BM-03 · 11 sensors', ms: 260 },
      { tool: 'searchLogbook', args: 'FDY-BM-03 · 14d', ms: 96 },
      { tool: 'findSimilarEvents', args: 'enterprise · 24m', ms: 340 },
    ],
    evidence: [
      { key: 'regression', value: 'r² 0.998 · η 4.5h' },
      { key: 'detectors', value: 'Δz 11.12σ · CUSUM 2.31' },
      { key: 'cascade', value: '4 sibling sensors' },
      { key: 'correlation', value: 'temp r = 0.87' },
      { key: 'priorEvents', value: '3 in 24 months' },
      { key: 'logbook', value: '02:14 · cell 3 noise' },
    ],
    similarEvents: [
      { alertId: 'ALR-4441', siteId: 'site_findlay', assetId: 'FDY-BM-07', outcome: 'plannedReplacement', avoidedHours: 6.0 },
      { alertId: 'ALR-3903', siteId: 'site_pune', assetId: 'PNQ-BM-02', outcome: 'plannedReplacement', avoidedHours: 5.5 },
    ],
    /** The 72h vibration trace. Synthetic, monotone by construction so the
        regression in `evidence` is honest about this data. */
    trace: [
      0.11, 0.11, 0.12, 0.11, 0.12, 0.13, 0.13, 0.14, 0.15, 0.16, 0.18, 0.19,
      0.21, 0.22, 0.24, 0.26, 0.28, 0.3, 0.32, 0.34, 0.36, 0.39, 0.41, 0.43,
      0.45, 0.47, 0.49, 0.52, 0.54, 0.57, 0.6,
    ],
    traceThreshold: 0.49,
    traceNormalBand: [0.06, 0.2],
    traceAnomalyFrom: 22,
    traceUnit: 'in/s',
  },
}

/** Safety gates. Any recommendation touching equipment defers to these. */
export const SAFETY_GATES = [
  { id: 'loto', assetId: 'FDY-BM-03', state: 'required', owner: 'usr_mgr_1' },
  { id: 'guardInterlock', assetId: 'FDY-BM-03', state: 'verified', owner: 'usr_ops_1' },
  { id: 'hotWorkPermit', assetId: 'FDY-OVN-01', state: 'awaitingSignoff', owner: 'usr_mgr_1' },
]
